import { Injectable, EffectRef } from "@angular/core";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../shared/providers/ref";
import { MapState } from "./map-state";
import { MapRepositoryProvider } from "../../../../shared/providers/repository-providers";
import { BasemapSwitcherProvider } from "../basemap-switcher/basemap-switcher-provider";
import { ViewMode } from "../../../../shared/enums/view-mode.enum";
import { ViewModeProvider } from "./view-mode-provider";
import { GraphicProvider } from "../graphic/graphic-provider";
import { MeasureProvider } from "../measure/measure-provider";
import ArcGISMap from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import Ground from "@arcgis/core/Ground";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import Basemap from "@arcgis/core/Basemap";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import type Graphic from "@arcgis/core/Graphic";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type GroupLayer from "@arcgis/core/layers/GroupLayer";
import type Layer from "@arcgis/core/layers/Layer";
import type FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";

/** Margen con el que se agranda el recuadro del cluster al volar hacia él (1.2 = 20% más). */
const CLUSTER_ZOOM_MARGIN = 1.2;
/**
 * Factor con el que se acerca un cluster sin área (un único punto agrupado): su recuadro es de
 * tamaño cero, así que `Extent.expand` no lo agranda y no hay área a la que volar.
 */
const CLUSTER_ZOOM_FACTOR = 2;

@Injectable()
export class MapNotifier extends BaseNotifier<MapState> {
  private static readonly INITIAL: MapState = {
    visibleCount: 0,
    view: null,
    map: null,
    viewRotation: 0,
    loading: true,
    scale: null,
  };

  private _map: ArcGISMap | null = null;
  private _mapView: MapView | null = null;
  private _sceneView: SceneView | null = null;
  private _view: MapView | SceneView | null = null;
  private mapReady = false;
  private _persistHandle: { remove(): void } | null = null;
  private _clusterHandle: { remove(): void } | null = null;
  /** Listener que invalida `_clusterLayers` cuando el árbol de capas cambia. */
  private _layerWatch: { remove(): void } | null = null;
  /** Capas agrupadas de la vista activa; `null` = hay que recalcularlas. */
  private _clusterLayers: FeatureLayer[] | null = null;
  private _persistTimer: ReturnType<typeof setTimeout> | null = null;
  private _initialView: {
    center: [number, number];
    zoom: number;
    rotation: number;
  } | null = null;
  private _modeSub: EffectRef | null = null;

  constructor(ref: Ref) {
    super(ref, MapNotifier.INITIAL);

    this.listen(() => {
      try {
        const state = this.snapshot();
        if (state.view) {
          const scale = state.view.scale;
          if (scale !== state.scale) {
            this.updateState((s) => ({ ...s, scale }));
          }
        }
      } catch (e) {
        console.error("[MapNotifier] Scale sync error:", e);
      }
    });
  }

  isReady(): boolean {
    return this.mapReady;
  }

  /**
   * Comando de navegación para consumidores del mapa (overview, futuros
   * paneles). La mutación pertenece al dueño: los externos no tocan la
   * instancia de la vista directamente.
   */
  goTo(target: Parameters<SceneView["goTo"]>[0]): void {
    const view = this._view;
    if (!view) return;
    // Los targets estructurales ({center, zoom, scale…}) son compatibles
    // entre vistas 2D/3D; la unión de TS exige el cast explícito.
    void (view as MapView)
      .goTo(target as Parameters<MapView["goTo"]>[0])
      .catch(() => {});
  }

  /**
   * Clic sobre una burbuja de agrupamiento: vuela al recuadro de los puntos que la componen.
   *
   * El popup del cluster está desactivado en el `featureReduction` (`popupEnabled: false`), así
   * que sin este manejador el clic no haría nada. Se consulta el recuadro con `aggregateIds`
   * (el ObjectID del gráfico agregado: `Query.aggregateIds` + `FeatureLayerView.queryExtent`) y
   * se navega con el margen del 20% para que la burbuja no quede pegada al borde. Si el recuadro
   * es un punto —un solo punto agrupado— no hay área a la que volar y se acerca un factor fijo:
   * `Extent.expand` no agranda un recuadro de tamaño cero.
   *
   * El manejador vive en el dueño de la vista (este notifier) para que funcione con el modo
   * identificación apagado o encendido; el pipeline de identificación ignora los clics sobre
   * agregados para no abrir un popup encima del vuelo (ver `PopupInteractionNotifier`).
   */
  private attachClusterClick(view: MapView | SceneView): void {
    this._clusterHandle?.remove();
    this._layerWatch?.remove();
    // La caché pertenece a la vista activa: al conmutar 2D/3D se recalcula desde cero.
    this._clusterLayers = null;
    // El conjunto de capas agrupadas solo cambia cuando el árbol de capas cambia, así que se
    // invalida con un listener en vez de recorrerlo en cada clic.
    this._layerWatch = view.map?.layers.on("change", () => {
      this._clusterLayers = null;
    }) ?? null;
    this._clusterHandle = view.on("click", (event) => {
      void this.zoomToCluster(view, event);
    });
  }

  private async zoomToCluster(view: MapView | SceneView, event: any): Promise<void> {
    try {
      // Con una herramienta modal activa (dibujo o medición) el clic pertenece a la herramienta
      // —agregar un vértice, tomar una distancia— y no al mapa: mover la cámara a mitad de
      // trazado invalidaría el trabajo del usuario.
      if (this.ref.watch(GraphicProvider).state().activeTool) return;
      if (this.ref.watch(MeasureProvider).state().activeTool) return;

      // Sin capas agrupadas y visibles no hay nada que acercar: se evita el hitTest completo
      // (que puede disparar consultas) en cada clic del mapa.
      const clusterLayers = this.clusterLayers(view);
      if (clusterLayers.length === 0) return;

      const response = await view.hitTest(event, { include: clusterLayers });
      const hit = (response.results as any[]).find(
        (result) => result.type === "graphic" && result.graphic?.isAggregate === true,
      );
      const graphic = hit?.graphic as Graphic | undefined;
      const layer = hit?.layer as FeatureLayer | undefined;
      const objectId = graphic?.getObjectId();
      if (!layer || objectId == null) return;

      const layerView = (await view.whenLayerView(layer)) as FeatureLayerView;
      const { extent } = await layerView.queryExtent({ aggregateIds: [objectId] });
      if (!extent) return;

      // Un cluster de un solo punto devuelve un recuadro de tamaño cero (y `expand` no lo
      // agranda): no hay área a la que volar, así que se acerca un factor fijo.
      if (!extent.width && !extent.height) {
        this.goTo({ center: extent.center, scale: view.scale / CLUSTER_ZOOM_FACTOR });
        return;
      }
      this.goTo(extent.clone().expand(CLUSTER_ZOOM_MARGIN));
    } catch (error) {
      console.warn("[MapNotifier] No se pudo acercar al cluster:", error);
    }
  }

  /**
   * FeatureLayers visibles con agrupamiento activo: acotan el `hitTest` del clic y permiten
   * saltarse el manejador cuando el mapa no tiene ninguna capa agrupada.
   */
  private clusterLayers(view: MapView | SceneView): FeatureLayer[] {
    if (this._clusterLayers) return this._clusterLayers;
    const layers = view.map?.layers;
    if (!layers) return [];

    const result: FeatureLayer[] = [];
    // `parentVisible` propaga la visibilidad efectiva: una capa agrupada dentro de un grupo
    // apagado no es visible aunque su propio `visible` sea true, y sin esta comprobación el
    // manejador seguiría lanzando `hitTest` sobre capas que no se dibujan.
    const visit = (nodes: Iterable<Layer>, parentVisible: boolean): void => {
      for (const layer of nodes) {
        const visible = parentVisible && layer.visible;
        if (layer.type === "group") {
          visit((layer as GroupLayer).layers, visible);
          continue;
        }
        const featureLayer = layer as FeatureLayer;
        if (visible && featureLayer.featureReduction?.type === "cluster") {
          result.push(featureLayer);
        }
      }
    };
    visit(layers as unknown as Iterable<Layer>, true);
    this._clusterLayers = result;
    return result;
  }

  setLoading(loading: boolean): void {
    this.updateState((s) => ({ ...s, loading }));
  }

  destroy(): void {
    this._modeSub?.destroy();
    this._modeSub = null;
    this._clusterHandle?.remove();
    this._clusterHandle = null;
    this._layerWatch?.remove();
    this._layerWatch = null;
    this._clusterLayers = null;
    this._persistHandle?.remove();
    this._persistHandle = null;
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
    if (this._mapView) {
      try {
        this._mapView.destroy();
      } catch {}
      this._mapView = null;
    }
    if (this._sceneView) {
      try {
        this._sceneView.destroy();
      } catch {}
      this._sceneView = null;
    }
    this._view = null;
    this._map = null;
    this.mapReady = false;
    this.updateState((s) => ({
      ...s,
      view: null,
      map: null,
      visibleCount: 0,
      loading: true,
      scale: null,
    }));
  }

  protected override onDispose(): void {
    this.destroy();
  }

  async initView(
    container2d: HTMLDivElement,
    container3d: HTMLDivElement,
  ): Promise<void> {
    const mapRepo = this.ref.watch(MapRepositoryProvider).instance;
    const r = await mapRepo.load();
    if (!r.success) return;
    const d = r.data!;

    const map = new ArcGISMap({
      ground: new Ground({
        layers: [new ElevationLayer({ url: d.elevation.url })],
      }),
    });

    const tiles = new WebTileLayer({
      urlTemplate: d.basemap.urlTemplate,
      copyright: d.basemap.copyright,
      subDomains:
        d.basemap.subDomains.length > 0 ? d.basemap.subDomains : undefined,
    });
    map.basemap = new Basemap({
      baseLayers: [tiles],
      id: d.basemap.id,
      title: d.basemap.title,
    });

    this._map = map;

    this._mapView = new MapView({
      container: container2d,
      map,
      center: d.center,
      zoom: d.zoom,
    });
    this._mapView.popup!.dockEnabled = false;
    this._mapView.ui.components = [];

    this._sceneView = new SceneView({
      container: container3d,
      map,
      center: d.center,
      zoom: d.zoom,
      qualityProfile: d.scene.qualityProfile as any,
      environment: {
        atmosphere: { quality: d.scene.atmosphereQuality } as any,
        starsEnabled: d.scene.starsEnabled,
        lighting: {
          directShadowsEnabled: d.scene.lighting.directShadowsEnabled,
          date: new Date(d.scene.lighting.date),
        } as any,
      } as any,
    });
    this._sceneView.popup!.dockEnabled = false;
    this._sceneView.ui.components = [];

    const initialState = {
      center: d.center,
      zoom: d.zoom,
      rotation: d.rotation,
    };
    this._initialView = initialState;

    await this._mapView.when();
    this._view = this._mapView;
    try {
      this._view.ui.remove("attribution");
    } catch {}

    this.updateState((s) => ({
      ...s,
      map: this._map!,
      view: this._view!,
      visibleCount: 0,
    }));
    this.mapReady = true;
    // Clic en una burbuja de agrupamiento → vuelo al recuadro de los puntos que la componen.
    this.attachClusterClick(this._mapView);

    this.ref.read(BasemapSwitcherProvider).notifier.syncActive(d.basemap.id);

    this._persistHandle?.remove();
    this._persistHandle = reactiveUtils.watch(
      () => this._view!.stationary,
      (stationary) => {
        if (!this._view) return;
        if (!stationary) return;
        const v = this._view;
        const lon = v.center?.longitude;
        const lat = v.center?.latitude;
        if (lon == null || lat == null) return;
        const atInitial =
          Math.abs(lon - initialState.center[0]) < 1e-6 &&
          Math.abs(lat - initialState.center[1]) < 1e-6 &&
          Math.abs(v.zoom - initialState.zoom) < 1e-6 &&
          Math.abs(((v as any).rotation ?? 0) - initialState.rotation) < 1e-6;
        if (atInitial) return;
        this.persistView();
      },
    );

    this._sceneView.when().catch(() => {});

    this._modeSub?.destroy();
    this._modeSub = this.listen(() => {
      const mode = this.ref.read(ViewModeProvider).state().mode;
      if (this.mapReady) this.switchTo(mode);
    });
  }

  async switchTo(mode: ViewMode): Promise<void> {
    if (!this._mapView || !this._sceneView) return;

    const newView = mode === ViewMode.Map ? this._mapView : this._sceneView;
    if (this._view === newView) return;

    const oldView = this._view;
    this._view = newView;
    this.updateState((s) => ({ ...s, view: newView }));
    // El manejador de clic viaja con la vista activa: el recuadro del cluster se calcula
    // sobre la vista que recibe el clic.
    this.attachClusterClick(newView);

    if (oldView) {
      const center = (oldView as any).center ?? oldView.extent?.center;
      const lat = center?.latitude ?? 0;
      const factor = Math.cos((lat * Math.PI) / 180);

      if (mode === ViewMode.Scene) {
        await (newView as SceneView).goTo(
          {
            target: center,
            tilt: 55,
            heading: (oldView as MapView).rotation ?? 0,
            scale: oldView.scale * factor,
          },
          { animate: true, duration: 800 },
        );
      } else {
        (newView as MapView).goTo({
          target: center,
          scale: oldView.scale / factor,
        });
      }
    }
  }

  getActiveView(): MapView | SceneView | null {
    return this._view;
  }

  getMapView(): MapView | null {
    return this._mapView;
  }

  getSceneView(): SceneView | null {
    return this._sceneView;
  }

  private persistView(): void {
    if (!this._view) return;
    if (this._persistTimer) clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(() => {
      const repo = this.ref.watch(MapRepositoryProvider).instance;
      const view = this._view!;
      const longitude = view.center.longitude;
      const latitude = view.center.latitude;
      if (longitude == null || latitude == null) return;
      repo
        .save({
          center: [longitude, latitude],
          zoom: view.zoom,
          rotation: (view as any).rotation ?? 0,
        })
        .catch(() => {});
    }, 500);
  }

  applyExternalState(updater: (prevState: MapState) => MapState): void {
    this.updateState(updater);
  }

  resetViewToInitial(): void {
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
    const repo = this.ref.watch(MapRepositoryProvider).instance;
    void repo.reset();
    const initial = this._initialView;
    const view = this._view;
    if (!initial || !view || view.destroyed) return;
    void view
      .goTo(
        {
          center: initial.center,
          zoom: initial.zoom,
          rotation: initial.rotation,
        },
        { animate: false },
      )
      .catch(() => {});
  }

  async applySharedView(params: URLSearchParams): Promise<void> {
    const view = this._view;
    if (!view) return;

    const zoom = params.get("zoom");
    const xmin = params.get("xmin");
    const ymin = params.get("ymin");
    const xmax = params.get("xmax");
    const ymax = params.get("ymax");
    const wkid = params.get("wkid");

    if (xmin && ymin && xmax && ymax) {
      const SpatialReference = (
        await import("@arcgis/core/geometry/SpatialReference")
      ).default;
      const Extent = (await import("@arcgis/core/geometry/Extent")).default;
      const sr = new SpatialReference({ wkid: wkid ? Number(wkid) : 102100 });
      const extent = new Extent({
        xmin: Number(xmin),
        ymin: Number(ymin),
        xmax: Number(xmax),
        ymax: Number(ymax),
        spatialReference: sr,
      });
      await view.goTo(extent, { animate: false });
    } else if (zoom) {
      await view.goTo({ zoom: Number(zoom) }, { animate: false });
    }
  }
}
